import { makeRequest } from "@/helpers/apiWrapper";
import { useAuthModalStore } from "@/Store/useAuthModalStore";
import { useUserStore } from "@/Store/UserInfoStore";
import Button from "@mui/material/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";
import CitiesDropDown from "../CitiesDropdown";
export default function SellerInfoModal() {
  const { user } = useUserStore.getState();
  const { closeStatusModal } = useAuthModalStore();

  const userInfo = user || {};
  const {
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: userInfo?.username || "",
      city: userInfo?.city || "",
      timezone: userInfo?.timezone || "",
      dob: userInfo?.dob || "",
    },
  });
  const [usernameValid, setUsernameValid] = useState(null);
  const [usernameError, setUsernameError] = useState("");
  const timeoutRef = useRef(null);
  const city = watch("city");

  const { data, isLoading } = useQuery({
    queryKey: ["timeZones"],
    queryFn: () =>
      makeRequest({
        endpoint: `/api/account/get-timezones/${userInfo?.country_iso_code}`,
        method: "GET",
        server: "main",
      }),
  });
  const timeZonesData = data?.timezones || [];

  const mutation = useMutation({
    mutationFn: async (username) => {
      const payload = { username };
      return makeRequest({
        endpoint: `/api/account/username-check`,
        payload,
        isEncrypted: true,
        method: "POST",
        server: "main",
      });
    },
    onSuccess: (data) => {
      if (data?.status) {
        setUsernameValid(true);
        setUsernameError("");
      } else {
        setUsernameValid(false);
        setUsernameError(data?.message || "Username is not available");
      }
    },
    onError: () => {
      setUsernameValid(false);
      setUsernameError("Something went wrong");
    },
  });

  const handleUsernameChange = (e) => {
    const value = e.target.value.trim().toLowerCase();
    setValue("username", value);

    if (!/^[a-zA-Z0-9_]*$/.test(value)) {
      setUsernameValid(false);
      setUsernameError("Only letters, numbers and underscores allowed");
      return;
    }

    if (value.length < 6) {
      setUsernameValid(false);
      setUsernameError("Username must be at least 6 characters");
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      mutation.mutate(value);
    }, 1000);
  };
  const UpdateNameMutation = useMutation({
    mutationFn: async (username) => {
      const payload = { username };
      return makeRequest({
        endpoint: `/api/account/update-username/${userInfo?._id}`,
        payload,
        isEncrypted: true,
        method: "PUT",
        server: "main",
      });
    },
  });
  const profileUpdateMutation = useMutation({
    mutationFn: async (payload: {
      city: string;
      timezone: string;
      dob: string;
    }) => {
      return makeRequest({
        endpoint: `/api/account/update-individual/${userInfo?._id}`,
        payload,
        isEncrypted: true,
        method: "PUT",
        server: "main",
      });
    },
    onSuccess: () => {
      toast.success("Profile updated successfully!");
      closeStatusModal();
    },
    onError: () => {
      toast.error("Failed to update profile!");
    },
  });
  const onSubmit = (data) => {
    if (usernameValid === false) {
      toast.error("Fix username first!");
      return;
    }

    const timezoneValue = data.timezone.split(" ")[0];
    const payload = {
      city: data.city,
      timezone: timezoneValue,
      dob: data.dob,
    };
    if (data.username !== userInfo.username) {
      UpdateNameMutation.mutate(data.username, {
        onSuccess: () => {
          profileUpdateMutation.mutate(payload);
        },
        onError: (err) => {
          toast.error(err?.message || "Failed to update username");
        },
      });
    } else {
      profileUpdateMutation.mutate(payload);
    }
  };
  return (
    <div className="p-8 w-full h-full bg-gradient-to-br from-white to-gray-50  rounded-2xl">
      <h2 className="text-3xl font-bold text-center text-black-700  mb-1 tracking-wide">
        Seller Information
      </h2>

      <p className="text-center text-gray-600 text-sm leading-relaxed  px-4">
        Please enter your details to complete your profile and unlock full
        access to
        <span className="font-semibold text-indigo-600"> eShop</span>. This
        helps us personalize your experience and keep your account secure.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-600">
            Username
          </label>
          <input
            type="text"
            placeholder="Enter Username"
            {...register("username", { required: "Username is required" })}
            onChange={handleUsernameChange}
            className={`w-full px-4 py-2 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 ${usernameValid === null
              ? "border-gray-300 focus:ring-indigo-500"
              : usernameValid
                ? "border-green-500 focus:ring-green-500"
                : "border-red-500 focus:ring-red-500"
              }`}
          />
          {(errors.username || usernameError) && (
            <span className="text-red-500 text-xs mt-1">
              {errors.username?.message || usernameError}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-600 mb-1">
            City
          </label>
          <CitiesDropDown
            value={city}
            setValue={(val) => setValue("city", val)}
            iso_3={userInfo?.country_iso_code || ""}
            isState={false}
            title="Select City"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-600 mb-1">
            Timezone
          </label>
          <select
            {...register("timezone", { required: "Timezone is required" })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-300"
          >
            <option value="">Select Timezone</option>
            {isLoading ? (
              <option>Loading...</option>
            ) : (
              timeZonesData?.map((tz, index) => (
                <option key={index} value={tz}>
                  {tz}
                </option>
              ))
            )}
          </select>
          {errors.timezone && (
            <span className="text-red-500 text-xs mt-1">
              {errors.timezone.message}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-semibold text-gray-600 mb-1">
            Date of Birth
          </label>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="dob"
              control={control}
              rules={{ required: "Date of Birth is required" }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  value={field.value || null}
                  onChange={(date) => field.onChange(date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.dob}
                      helperText={errors.dob?.message}
                      className="bg-white"
                    />
                  )}
                />
              )}
            />
          </LocalizationProvider>
        </div>

        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{
            mt: 3,
            borderRadius: "12px",
            background: "#5426da",
            textTransform: "none",
            fontWeight: "bold",
            letterSpacing: "0.5px",
            "&:hover": {
              background: "linear-gradient(to right, #4F46E5, #4338CA)",
            },
          }}
        >
          Submit
        </Button>
      </form>
    </div>
  );
}
